import React, { useEffect, useState } from "react";
import { useService } from "../../../context/ServiceContext";
import {
  AlertTriangle,
  Download,
  EyeIcon,
  Loader,
  User2Icon,
} from "lucide-react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { fetchProfile } from "../../../Services/profile/profileservice";
import { isAlpha, isAlphanumeric, isNumeric } from "validator";
import {
  downloadExperianPDF,
  fetchStateService,
  getExperianPdf,
} from "../../../Services/CreditScorePDF/CreditScorePdf";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";

function ExperianPdfUrl() {
  const [name, setName] = useState(null);
  const [pan, setPan] = useState(null);
  const [mobile, setMobile] = useState(null);
  const [gender, setGender] = useState(null);
  const [state, setState] = useState(null);
  const [dob, setDob] = useState(null);
  const [address, setAddress] = useState(null);
  const [pincode, setPincode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [experianData, setExperianData] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [statesList, setStatesList] = useState(null);

  const getProfileData = async () => {
    try {
      setIsLoading(true);

      const res = await fetchProfile();

      if (res?.data) {
        setProfileData(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProfileData();
  }, []);

  const isNumber = (value) => {
    if (!value) return false;

    return isNumeric(value, { no_symbols: true });
  };

  const isAlphabetic = (value) => {
    if (!value) return;

    return isAlpha(String(value).trim(), "en-IN", { ignore: " " });
  };

  const isAlphaNumericValues = (value) => {
    if (!value) return;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const isValidDate = (dateString) => {
    if (!dateString) return false;

    // Check if the date is in YYYY-MM-DD format (HTML date input format)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    // Parse the date and check if it's valid
    const date = new Date(dateString);
    const timestamp = date.getTime();

    // Check if the date is invalid
    if (typeof timestamp !== "number" || Number.isNaN(timestamp)) {
      return false;
    }

    // Check if the date is not in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today) {
      return false;
    }

    // Check if the date is reasonable (not too old, e.g., before 1900)
    const minDate = new Date("1900-01-01");
    if (date < minDate) {
      return false;
    }

    // Check if the parsed date matches the input (prevents invalid dates like 2023-02-30)
    const [year, month, day] = dateString.split("-").map(Number);
    if (
      date.getFullYear() !== year ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return false;
    }

    return true;
  };

  const getExperianPdfDetails = async () => {
    if (!profileData) return;

    const validateNumber = isNumber(mobile);
    const validateName = isAlphabetic(name);
    const validatePan = isAlphaNumericValues(pan);
    const validatePincode = isNumber(pincode);

    if (!mobile) {
      return setInputError("⚠️ Please enter Mobile No.");
    } else if (!validateNumber) {
      return setInputError("⚠️ Please enter numbers only in Mobile No.");
    } else if (mobile.length < 10) {
      return setInputError("⚠️ Enter at least 10 digits in Mobile No.");
    } else if (!name) {
      return setInputError("⚠️ Please enter Full Name");
    } else if (!validateName) {
      return setInputError("⚠️ Enter only characters in Full Name");
    } else if (name.length < 3) {
      return setInputError("⚠️ Enter at least 3 characters in Full Name");
    } else if (!pan) {
      return setInputError("⚠️ Please enter PAN");
    } else if (!validatePan) {
      return setInputError("⚠️ Enter only alphanumeric values in PAN");
    } else if (pan.length < 10) {
      return setInputError("⚠️ Enter at least 10 characters in PAN");
    } else if (!gender) {
      return setInputError("⚠️ Please select Gender");
    } else if (!state) {
      return setInputError("⚠️ Please select State");
    } else if (!pincode) {
      return setInputError("⚠️ Please enter Pin Code");
    } else if (!validatePincode) {
      return setInputError("⚠️ Please enter Numbers only");
    } else if (!address) {
      return setInputError("⚠️ Please enter Address");
    }
    if (dob === "") {
      return setInputError("Date of Birth is required");
    } else if (!isValidDate(dob)) {
      return setInputError("Enter a valid date of birth");
    }

    setInputError(null);

    try {
      setIsLoading(true);

      console.log("getting transunion details  ++++++++++++++++++++++");

      console.log(
        profileData.user_id,
        profileData.api_key,
        profileData.token_id,
      );

      const res = await getExperianPdf({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        name,
        pan,
        mobile,
        gender,
        state,
        dob,
        address,
        pincode,
        consent: "Y",
      });

      if (res) {
        setExperianData(res);
        setPdfUrl(res?.data?.report_url);
      }

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "request successful");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.message || "Invalid format");
      } else if (res?.status?.code === 401) {
        showErrorToast(res?.message || "Unauthorized");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.message || "server error");
      } else {
        showErrorToast(res?.status?.message || "Verification failed");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // download PDF
  const handleDownload = async () => {
    if (!pdfUrl) {
      showErrorToast("No PDF URL available");
      return;
    }

    setIsLoading(true);
    setIsDownloading(true);

    try {
      const blob = await downloadExperianPDF(pdfUrl);
      console.log("blob", blob);
      const url = URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = url;
      link.download = `download transunion pdf ${Date.now()}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccessToast("PDF downloaded successfully");

      console.log("downloaded successfully");
    } catch (error) {
      console.log(error);

      showErrorToast(" Failed to download PDF");
    } finally {
      setIsLoading(false);
      setIsDownloading(false);
    }
  };

  // View PDF
  const handleViewPDF = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } else {
      showErrorToast("No PDF URL available");
    }
  };

  useEffect(() => {
    async function fetchStates() {
      try {
        const response = await fetchStateService();
        setStatesList(response.data || []);
      } catch (error) {
        console.error("Error fetching states", error);
      }
    }
    fetchStates();
  }, []);

  return (
    <>
      <Seo />
      <Header heading="Experian PDF" />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="flex flex-col min-h-screen items-center pt-4 lg:pt-8 justify-start gap-6 bg-gray-50 px-4">
        {/* form */}

        <div className="w-full flex flex-col gap-6 justify-start items-center rounded-2xl bg-white p-6 shadow-lg">
          <h2 className="mb-6 text-center text-2xl font-semibold text-gray-800 flex items-center justify-center gap-3">
            <User2Icon />
            Enter User Details
          </h2>

          <div className=" gap-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {/* Name */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="rounded-lg border  px-2 py-2 text-gray-800 outline-none  shadow-sm shadow-blue-600  border-blue-600 transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>

            {/* Mobile */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Mobile
              </label>
              <input
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                maxLength={10}
                type="text"
                placeholder="Enter mobile number"
                className="rounded-lg border  px-2 py-2 text-gray-800 outline-none  shadow-sm shadow-blue-600  border-blue-600 transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>

            {/* PAN */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                PAN
              </label>
              <input
                value={pan}
                maxLength={10}
                onChange={(e) => setPan(e.target.value.toUpperCase())}
                type="text"
                placeholder="ABCDE1234F"
                className="uppercase rounded-lg  shadow-sm shadow-blue-600 border border-blue-600  px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>

            {/* DOB */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Date of birth
              </label>
              <input
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                type="date"
                max={new Date().toISOString().split("T")[0]}
                min="1900-01-01"
                className="uppercase rounded-lg  shadow-sm shadow-blue-600 border border-blue-600  px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>

            {/* Gender */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Gender
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="rounded-lg  shadow-sm shadow-blue-600 border border-blue-600  px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            {/* State */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                State
              </label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="rounded-lg  shadow-sm shadow-blue-600 border border-blue-600  px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              >
                <option value="">Select State</option>
                {statesList?.map((state) => (
                  <option key={state.id} value={state.abbreviation}>
                    {state.state_name}
                  </option>
                ))}
              </select>
            </div>

            {/* pin */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Pin code
              </label>
              <input
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                maxLength={6}
                type="text"
                placeholder="Enter pin code"
                className="rounded-lg  shadow-sm shadow-blue-600 border border-blue-600  px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>

            {/* address */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Address
              </label>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                type="text"
                placeholder="Enter Address"
                className="rounded-lg  shadow-sm shadow-blue-600 border border-blue-600  px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <button
          type="button"
          onClick={getExperianPdfDetails}
          className="mt-6 px-6 rounded-lg bg-gradient-to-br from-purple-700 to-blue-500 py-2.5 text-white font-medium transition hover:opacity-90 active:scale-[0.98]"
        >
          Continue
        </button>

        {/* response */}

        {experianData && (
          <div>{experianData?.data?.message || experianData.message}</div>
        )}

        {/* input error */}

        {inputError && (
          <div className="text-red-700 text-2xl font-extrabold  ">
            {inputError}
          </div>
        )}

        {/* {experianData && (
          <div className="text-green-700 text-2xl w-[300px] font-extrabold  ">
            {JSON.stringify(experianData)}
          </div>
        )} */}
        {/* 
        {experianData && (
          <div>
            <div className="text-yellow-700 text-2xl font-extrabold  ">
              {experianData?.status?.code}
            </div>

            <div className="text-blue-700 text-2xl font-extrabold  ">
              {experianData?.data?.status?.code}
            </div>

            <div className="text-cyan-700 text-2xl font-extrabold  ">
              {experianData?.report_url}
            </div>
            <div className="text-cyan-700 text-2xl font-extrabold  ">
              {experianData?.credit_score}
            </div>
          </div>
        )} */}

        {/* message */}

        {experianData && (
          <div className="text-green-700 text-2xl font-extrabold  ">
            {experianData?.message || "got experian data successfully"}
          </div>
        )}

        {experianData?.status?.code === 200 && (
          <div className="flex flex-col items-center gap-6 py-6 justify-center">
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 lg:gap-8 xl:gap-10">
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className={`flex items-center gap-2 py-2 px-4 bg-gradient-to-br rounded-lg ${isDownloading ? "from-gray-200 to-gray-50 text-black" : " from-purple-700 to-blue-600 text-white"}`}
              >
                <Download className="animate-bounce" />
                <p className="font-semibold text-2xl tracking-wider">
                  {" "}
                  {isDownloading ? "Downloading" : "Download Now"}
                </p>
              </button>

              <button
                onClick={handleViewPDF}
                className="flex items-center gap-2 py-2 px-4 bg-gradient-to-br rounded-lg from-purple-700 to-blue-600 text-white"
              >
                <EyeIcon className="animate-pulse" />
                <p className="font-semibold text-2xl tracking-wider"> View</p>
              </button>
            </div>
          </div>
        )}

        {/* instructions */}

        <div>
          <div className="bg-gradient-to-br from-purple-100 to-blue-100 text-black border p-5 mt-12 rounded-md flex flex-col items-start gap-2">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <AlertTriangle />
              Important Instructions <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed flex items-start justify-start gap-3">
              <span>●</span>
              Please enter valid Name, Mobile, Pan, Date Of Birth Gender, State,
              Address, Pin.
            </p>
            <p className="text-sm leading-relaxed flex items-start justify-start gap-3">
              <span>●</span>
              After successful verification, the system will return the PDF as
              response.
            </p>
            <p className="text-sm leading-relaxed flex items-start justify-start gap-3">
              <span>●</span>
              You may review and download the PDF for your reference or further
              use.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default ExperianPdfUrl;
